import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Select from "react-select";
import { Row, Col, Card, Button, Space, Typography, Table, Input, Modal, Checkbox, Dropdown, Tooltip, InputNumber, Empty, Tag, Popconfirm, Switch } from "antd";
import _ from "lodash";
import { faEdit, faPlug, faPlus, faUserSlash } from "@fortawesome/free-solid-svg-icons";

function Users(props) {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState(null);

  const [selectedUser, setSelectedUser] = useState(null);

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [modalWidth, setModalWidth] = useState(900);

  const [billingGroups, setBillingGroups] = useState([]);
  const [securityProfiles, setSecurityProfiles] = useState([]);

  const [showInactive, setShowInactive] = useState(false);

  const user_table_columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: (a, b) => (a.name > b.name ? 1 : -1),
      defaultSortOrder: "ascend",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      render: (value, object) => (object.deleted_at ? <Tag color="#B63B45">Deactivated User</Tag> : value),
    },
    {
      title: "Billing Group",
      dataIndex: "billing_groups_name",
      key: "billing_groups_name",
    },
    {
      title: "Security Profile",
      dataIndex: "security_profiles_name",
      key: "security_profiles_name",
    },
    {
      title: "Last Login",
      dataIndex: "last_login",
      key: "last_login",
      render: (value, object) =>
        value ? (
          props.MakeDateTimeReadable(value, 2)
        ) : (
          <Popconfirm
            title="Resend Signup Email?"
            description="Are you sure you want to resend the signup email?"
            onConfirm={() => {
              ResendSignUpEmail(object.id);
            }}
          >
            <Tag className="clickable">No Login</Tag>
          </Popconfirm>
        ),
    },
    {
      title: "Status",
      dataIndex: "deleted_at",
      key: "deleted_at",
      render: (value, object) => (value ? <Tag color="#B63B45">Deactivated User</Tag> : <Tag color="#87d068">Active</Tag>),
    },
    {
      title: "Actions",
      align: "right",
      dataIndex: "id",
      key: "id",
      render: (value, object) => (
        <Space>
          <Button
            size="small"
            type="primary"
            onClick={() => {
              let selected_user = users.find((f) => f.id === value);
              let found_billing_group = billingGroups.find((f) => f.id === selected_user?.billing_groups_xref);
              let found_security_profile = securityProfiles.find((f) => f.id === selected_user?.security_profiles_xref);
              setSelectedUser({ ...selected_user, billing_group: found_billing_group, security_profile: found_security_profile });
              setEditModalOpen(true);
            }}
          >
            <FontAwesomeIcon icon={faEdit} className="mr-1" />
            Edit
          </Button>
          {!object.last_login && !object.deleted_at ? (
            <Popconfirm
              title="Resend Signup Email?"
              description="Are you sure you want to resend the signup email?"
              onConfirm={() => {
                ResendSignUpEmail(object.id);
              }}
            >
              <Button size="small">Resend Signup Email</Button>
            </Popconfirm>
          ) : null}
          {object.deleted_at ? (
            <Popconfirm
              title="Are you sure?"
              description={"Are you sure you want to reactivate " + object.name + "?"}
              onConfirm={() => {
                ReactivateUser(object.id);
              }}
            >
              <Button size="small">
                <FontAwesomeIcon icon={faUserSlash} className="mr-1" />
                Reactivate
              </Button>
            </Popconfirm>
          ) : (
            <Popconfirm
              title="Are you sure?"
              description={"Are you sure you want to deactivate " + object.name + "?"}
              onConfirm={() => {
                DeleteUser(object.id);
              }}
            >
              <Button size="small" danger>
                <FontAwesomeIcon icon={faUserSlash} className="mr-1" />
                Deactivate
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];
  const focusFirst = useRef();

  const GetUsers = async () => {
    let response = await props.AxiosRequest("/api/users/get", { show_inactive: showInactive });
    setUsers(
      response.data.users
        .map((m) => {
          return { ...m, value: m.id, label: m.name };
        })
        .sort((a, b) => {
          return ("" + a.name).localeCompare(b.name);
        }),
    );
    setFilteredUsers(response.data.users);
  };
  const UpdateUser = async () => {
    if (
      selectedUser?.first_name?.length === 0 ||
      selectedUser?.last_name?.length === 0 ||
      selectedUser?.email?.length === 0 ||
      !selectedUser?.billing_groups_xref ||
      !selectedUser?.security_profiles_xref
    )
      return props.MakeNotification("info", "Please Enter All Required Fields.");
    await props.AxiosRequest("/api/users/update", { ...selectedUser });
    GetUsers();
    setEditModalOpen(false);
    setSelectedUser(null);
  };
  const DeleteUser = async (id) => {
    await props.AxiosRequest("/api/users/delete", { id });
    GetUsers();
  };
  const ReactivateUser = async (id) => {
    await props.AxiosRequest("/api/users/reactivate", { id });
    GetUsers();
  };
  const AddUser = async () => {
    if (
      selectedUser?.first_name?.length === 0 ||
      selectedUser?.last_name?.length === 0 ||
      selectedUser?.email?.length === 0 ||
      !selectedUser?.billing_groups_xref ||
      !selectedUser?.security_profiles_xref
    )
      return props.MakeNotification("info", "Please Enter All Required Fields.");
    await props.AxiosRequest("/api/users/insert", { ...selectedUser });
    GetUsers();
    setAddModalOpen(false);
    setSelectedUser(null);
  };
  const ResendSignUpEmail = async (id) => {
    await props.AxiosRequest("/api/user/resend-signup-email", { id });
  };

  const GetSecurityProfiles = async () => {
    let response = await props.AxiosRequest("/api/security-profiles/get");
    setSecurityProfiles(
      response.data.security_profiles.map((m) => {
        return { ...m, label: m.name, value: m.id };
      }),
    );
  };
  const GetBillingGroups = async () => {
    let response = await props.AxiosRequest("/api/billing-groups/get");
    setBillingGroups(
      response.data.billing_groups.map((m) => {
        return { ...m, label: m.name, value: m.id };
      }),
    );
  };

  useEffect(() => {
    GetUsers();
    GetSecurityProfiles();
    GetBillingGroups();
  }, []);

  useEffect(() => {
    GetUsers();
  }, [showInactive]);

  useEffect(() => {
    if (searchTerm?.length === 0) return setFilteredUsers(users);
    let filtered_list = users.filter((f) => (_.toLower(f.name).indexOf(_.toLower(searchTerm)) > -1 ? true : false));
    setFilteredUsers(filtered_list);
  }, [searchTerm]);

  useEffect(() => {
    const resize = () => {
      const maxSize = Math.min(900, window.innerWidth);
      setModalWidth(maxSize);
    };
    window.addEventListener("resize", resize);
  });

  return (
    <main className="content">
      <div className="container-fluid">
        <div className="header">
          <h1 className="header-title">Users</h1>
        </div>
        <Row gutter={[16, 16]}>
          <Card
            title={<>Users</>}
            style={{
              width: "100%",
            }}
            extra={
              <Button
                type="primary"
                onClick={() => {
                  setSelectedUser(null);
                  setAddModalOpen(true);
                }}
              >
                <FontAwesomeIcon icon={faPlus} className="me-2" /> Add A New User
              </Button>
            }
          >
            <Row>
              <Col span={18}></Col>
              <Col span={6}>
                <Input
                  className="mb-2"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                  }}
                  placeholder="Search..."
                  allowClear
                />
              </Col>

              <Col span={24}>
                <Table className="table table-striped hover" columns={user_table_columns} dataSource={filteredUsers} />
              </Col>
              <Col span={24} className="text-right">
                <label className="col-form-label-sm w-100">Show Deactivated Users</label>
                <Switch checked={showInactive} onChange={(e) => setShowInactive(e)} />
              </Col>
            </Row>
          </Card>
        </Row>
        <Modal
          title={"Edit User"}
          centered
          width={modalWidth}
          open={editModalOpen}
          onCancel={() => {
            setEditModalOpen(false);
            setSelectedUser(null);
          }}
          footer={
            <>
              <Button
                onClick={() => {
                  setEditModalOpen(false);
                  setSelectedUser(null);
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  UpdateUser();
                }}
              >
                Save Changes
              </Button>
            </>
          }
        >
          {selectedUser ? (
            <>
              <Row gutter={[{ xs: 8, sm: 16, md: 24, lg: 32 }, 8]}>
                <Col span={6}>
                  <label className="col-form-label-sm">First Name *</label>
                  <Input
                    value={selectedUser.first_name}
                    onChange={(e) => {
                      setSelectedUser({ ...selectedUser, first_name: e.target.value });
                    }}
                  />
                </Col>
                <Col span={6}>
                  <label className="col-form-label-sm">Last Name *</label>
                  <Input
                    value={selectedUser.last_name}
                    onChange={(e) => {
                      setSelectedUser({ ...selectedUser, last_name: e.target.value });
                    }}
                  />
                </Col>
                <Col span={12}>
                  <label className="col-form-label-sm">Email *</label>
                  <Input
                    value={selectedUser.email}
                    onChange={(e) => {
                      setSelectedUser({ ...selectedUser, email: e.target.value });
                    }}
                  />
                </Col>
                <Col span={12}>
                  <label className="col-form-label-sm">Billing Group *</label>
                  <Select
                    value={selectedUser.billing_group}
                    options={billingGroups}
                    onChange={(e) => {
                      setSelectedUser({ ...selectedUser, billing_group: e, billing_groups_xref: e.value });
                    }}
                  />
                </Col>
                <Col span={12}>
                  <label className="col-form-label-sm">Security Profile *</label>
                  <Select
                    value={selectedUser.security_profile}
                    options={securityProfiles}
                    onChange={(e) => {
                      setSelectedUser({ ...selectedUser, security_profile: e, security_profiles_xref: e.value });
                    }}
                  />
                </Col>
              </Row>
            </>
          ) : null}
        </Modal>
        <Modal
          title={"Add A New User"}
          centered
          width={modalWidth}
          open={addModalOpen}
          onCancel={() => {
            setAddModalOpen(false);
            setSelectedUser(null);
          }}
          afterOpenChange={(addModalOpen) => addModalOpen && focusFirst.current?.focus()}
          footer={
            <>
              <Button
                onClick={() => {
                  setAddModalOpen(false);
                  setSelectedUser(null);
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                onClick={() => {
                  AddUser();
                }}
              >
                Add User
              </Button>
            </>
          }
        >
          <Row gutter={[{ xs: 8, sm: 16, md: 24, lg: 32 }, 8]}>
            <Col span={6}>
              <label className="col-form-label-sm">First Name *</label>
              <Input
                ref={focusFirst}
                value={selectedUser?.first_name}
                onChange={(e) => {
                  setSelectedUser({ ...selectedUser, first_name: e.target.value });
                }}
              />
            </Col>
            <Col span={6}>
              <label className="col-form-label-sm">Last Name *</label>
              <Input
                value={selectedUser?.last_name}
                onChange={(e) => {
                  setSelectedUser({ ...selectedUser, last_name: e.target.value });
                }}
              />
            </Col>
            <Col span={12}>
              <label className="col-form-label-sm">Email *</label>
              <Input
                value={selectedUser?.email}
                onChange={(e) => {
                  setSelectedUser({ ...selectedUser, email: e.target.value });
                }}
              />
            </Col>
            <Col span={12}>
              <label className="col-form-label-sm">Billing Group *</label>
              <Select
                value={selectedUser?.billing_group || null}
                options={billingGroups}
                onChange={(e) => {
                  setSelectedUser({ ...selectedUser, billing_group: e, billing_groups_xref: e.value });
                }}
              />
            </Col>
            <Col span={12}>
              <label className="col-form-label-sm">Security Profile *</label>
              <Select
                value={selectedUser?.security_profile || null}
                options={securityProfiles}
                onChange={(e) => {
                  setSelectedUser({ ...selectedUser, security_profile: e, security_profiles_xref: e.value });
                }}
              />
            </Col>
          </Row>
        </Modal>
      </div>
    </main>
  );
}

export default Users;
